import { Injectable } from '@angular/core';
import { IDBPDatabase, openDB } from 'idb';
import {
  eProvider,
  oClient,
  oContract,
  oUser,
  tyClientContract,
} from 'ravcredit-core';
import {
  dbAuthStore,
  dbBusinessConfig,
  dbBusinessInfo,
  dbClientsStore,
  dbConektaClientsStore,
  dbConektaReferencesStore,
  dbContractsStore,
  dbDynamicReferencesStore,
  dbIndexAuth,
  dbIndexBusiness,
  dbIndexConektaClientsRef,
  dbIndexNotification,
  dbIndexPassportClientsRef,
  dbIndexPayments,
  dbIndexUsers,
  dbName,
  dbNotificationsStore,
  dbPassportReferencesStore,
  dbPaymentsStore,
  dbUsersStore,
  dbVersion,
  keyPathCurp,
  keyPathId,
  keyPathReference,
  keyUserID,
} from '../util/config';

@Injectable({
  providedIn: 'root',
})
export class IndexedDbService {
  indexDbStatus = false;
  iDb!: IDBPDatabase<unknown>;

  constructor() {
    if ('indexedDB' in window) {
      this.indexDbStatus = true;
      this.initDB().then();
    } else {
    }
  }

  async initDB() {
    this.iDb = await openDB(dbName, dbVersion, {
      upgrade(db: IDBPDatabase) {
        if (!db.objectStoreNames.contains(dbAuthStore)) {
          db.createObjectStore(dbAuthStore, {
            keyPath: 'id',
            autoIncrement: true,
          }).createIndex(dbIndexAuth, dbIndexAuth, { unique: true });
        }

        if (!db.objectStoreNames.contains(dbUsersStore)) {
          db.createObjectStore(dbUsersStore, {
            keyPath: 'id',
            autoIncrement: true,
          }).createIndex(dbIndexUsers, dbIndexUsers, { unique: true });
        }

        if (!db.objectStoreNames.contains(dbContractsStore)) {
          const storeContract = db.createObjectStore(dbContractsStore, {
            keyPath: keyPathId,
            autoIncrement: true,
          });
          storeContract.createIndex(keyPathId, keyPathId, { unique: true });
        }

        if (!db.objectStoreNames.contains(dbClientsStore)) {
          const storeClient = db.createObjectStore(dbClientsStore, {
            keyPath: [keyPathId, keyPathCurp],
            autoIncrement: false,
          });
          storeClient.createIndex(keyPathId, keyPathId, { unique: true });
          storeClient.createIndex(keyPathCurp, keyPathCurp, { unique: true });
        }

        if (!db.objectStoreNames.contains(dbConektaClientsStore)) {
          const storeConekta = db.createObjectStore(dbConektaClientsStore, {
            keyPath: [keyPathId, keyPathCurp],
            autoIncrement: false,
          });
          storeConekta.createIndex(keyPathId, keyPathId, { unique: true });
          storeConekta.createIndex(keyPathCurp, keyPathCurp, { unique: true });
        }

        if (!db.objectStoreNames.contains(dbConektaReferencesStore)) {
          db.createObjectStore(dbConektaReferencesStore, {
            keyPath: 'id',
            autoIncrement: true,
          }).createIndex(dbIndexConektaClientsRef, dbIndexConektaClientsRef, {
            unique: true,
          });
        }

        if (!db.objectStoreNames.contains(dbDynamicReferencesStore)) {
          const storeConekta = db.createObjectStore(dbDynamicReferencesStore, {
            keyPath: [keyPathId, keyPathReference],
            autoIncrement: false,
          });
          storeConekta.createIndex(keyPathId, keyPathId, { unique: true });
          storeConekta.createIndex(keyPathReference, keyPathReference, {
            unique: true,
          });
        }

        if (!db.objectStoreNames.contains(dbPassportReferencesStore)) {
          db.createObjectStore(dbPassportReferencesStore, {
            keyPath: 'id',
            autoIncrement: true,
          }).createIndex(dbIndexPassportClientsRef, dbIndexPassportClientsRef, {
            unique: true,
          });
        }

        if (!db.objectStoreNames.contains(dbBusinessInfo)) {
          db.createObjectStore(dbBusinessInfo, {
            keyPath: 'version',
          }).createIndex(dbIndexBusiness, dbIndexBusiness, { unique: true });
        }

        if (!db.objectStoreNames.contains(dbBusinessConfig)) {
          db.createObjectStore(dbBusinessConfig, {
            keyPath: 'version',
          }).createIndex(dbIndexBusiness, dbIndexBusiness, { unique: true });
        }

        if (!db.objectStoreNames.contains(dbNotificationsStore)) {
          db.createObjectStore(dbNotificationsStore, {
            keyPath: 'notification_id',
          }).createIndex(dbIndexNotification, dbIndexNotification, {
            unique: true,
          });
        }

        if (!db.objectStoreNames.contains(dbPaymentsStore)) {
          db.createObjectStore(dbPaymentsStore, { keyPath: 'id' }).createIndex(
            dbIndexPayments,
            dbIndexPayments,
            { unique: true },
          );
        }
      },
    });
  }

  // ============ PRIVATE HELPERS ============
  private async _executeDbOperation<T>(
    operation: (db: IDBPDatabase<unknown>) => Promise<T>,
  ): Promise<T> {
    const db = await openDB(dbName, dbVersion);
    try {
      return await operation(db);
    } finally {
      db.close();
    }
  }

  // ============ STORAGE HELPERS ============
  setLocalStorage(k: string, v: string): void {
    localStorage.setItem(k, v);
  }

  getLocalStorage<T>(k: string): T | null {
    return localStorage.getItem(k) as T | null;
  }

  getLocalUser(): string | null {
    return localStorage.getItem(keyUserID);
  }

  // ============ GENERIC CRUD OPERATIONS ============
  async add<T>(store: string, data: T): Promise<boolean> {
    try {
      await this._executeDbOperation((db) => db.put(store, data));
      return true;
    } catch (error) {
      console.error(`Error adding to ${store}:`, error);
      return false;
    }
  }

  async get<T>(store: string, key: any): Promise<T | undefined> {
    if (!key) return undefined;
    return this._executeDbOperation((db) => db.get(store, key) as Promise<T>);
  }

  async getAll<T>(store: string): Promise<T[]> {
    return this._executeDbOperation((db) => db.getAll(store) as Promise<T[]>);
  }

  async update<T>(store: string, data: T): Promise<T> {
    try {
      await this._executeDbOperation((db) => db.put(store, data));
      return data;
    } catch (error) {
      console.error(`Error updating ${store}:`, error);
      return {} as T;
    }
  }

  async upsert<T>(store: string, key: any, data: T): Promise<any> {
    return this._executeDbOperation(async (db) => {
      const existing = await db.get(store, key);
      if (existing) {
        await db.delete(store, key);
      }
      return db.add(store, data);
    });
  }

  async delete<T>(store: string, key: any): Promise<T> {
    return this._executeDbOperation(
      (db) => db.delete(store, key) as Promise<T>,
    );
  }

  async deleteByIndex<T>(
    store: string,
    indexName: string,
    key: any,
  ): Promise<void> {
    return this._executeDbOperation(async (db) => {
      const txn = db.transaction(store, 'readwrite');
      const index = txn.objectStore(store).index(indexName);
      let cursor = await index.openCursor(key);
      while (cursor) {
        cursor.delete();
        cursor = await cursor.continue();
      }
    });
  }

  async clear<T>(store: string): Promise<void> {
    return this._executeDbOperation((db) => db.clear(store));
  }

  async bulkAddOrUpdate<T>(store: string, dataList: T[]): Promise<void> {
    for (const data of dataList) {
      const exists = await this.get(store, (data as any)['id']);
      if (!exists) {
        await this.add(store, data);
      } else {
        await this.update(store, data);
      }
    }
  }

  // ============ AUTHENTICATION HELPERS ============
  async dbSignIn<T extends oUser>(store: string, data: T): Promise<void> {
    try {
      await this._executeDbOperation((db) => db.put(store, data));
      this.setLocalStorage(keyUserID, data.id);
    } catch (error) {
      console.error(`Error signing in to ${store}:`, error);
      throw error;
    }
  }

  async dbAuthorization(): Promise<oUser | null> {
    try {
      const user = await this.getAuthUser();
      return user || null;
    } catch (error) {
      console.error('Authorization check failed:', error);
      return null;
    }
  }

  async getAuthUser(): Promise<oUser | undefined> {
    const users = await this.getAll<oUser>(dbAuthStore);
    return users?.[0];
  }

  async getAuthName(): Promise<string> {
    const user = await this.getAuthUser();
    return user?.name || '';
  }

  async getAuthUsername(): Promise<string> {
    const user = await this.getAuthUser();
    return user?.username || '';
  }

  async getPlatformUser(): Promise<string> {
    const userId = this.getLocalStorage<string>(keyUserID);
    if (!userId) return '';

    const user = await this.get<oUser>(dbAuthStore, userId);
    return user?.name || '';
  }

  async getToken(): Promise<string> {
    const userId = this.getLocalUser();
    if (!userId) return '';

    const user = await this.get<oUser>(dbAuthStore, userId);
    return user?.access_token || '';
  }

  // ============ DOMAIN SPECIFIC OPERATIONS ============
  async saveDataLocally<T>(
    contract: oContract,
    client: oClient,
    providerData: T,
  ): Promise<boolean> {
    try {
      await this.add(dbClientsStore, client);
      await this.add(dbContractsStore, contract);

      if (!providerData) return false;

      const { provider } = contract.financial;
      if (provider === eProvider.Conekta) {
        await this.add(dbConektaClientsStore, providerData);
        await this.add(dbConektaReferencesStore, providerData);
      } else if (provider === eProvider.Passport) {
        await this.add(dbPassportReferencesStore, providerData);
      } else if (provider === eProvider.DynamiCore) {
        await this.add(dbDynamicReferencesStore, providerData);
      }
      return true;
    } catch (error) {
      console.error('Error saving data locally:', error);
      return false;
    }
  }

  async getAllClientContracts(): Promise<tyClientContract[]> {
    const clients = await this.getAll<oClient>(dbClientsStore);
    if (!clients || clients.length === 0) return [];

    const results = await Promise.all(
      clients.map(async (client) => {
        const contract = await this.get<oContract>(
          dbContractsStore,
          client.contract,
        );
        return {
          ...client,
          ...contract,
          id: client.id,
          status: contract?.status,
        } as tyClientContract;
      }),
    );

    return results;
  }
}
